import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Search, Users, ArrowRight, Calendar, Mail, Phone } from 'lucide-react';

const CustomersPage = () => {
  const { api, business } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (business?.id) {
      fetchCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const fetchCustomers = async () => {
    try {
      const params = {};
      if (search) params.search = search;

      const response = await api.get(`/businesses/${business.id}/customers`, { params });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchCustomers();
  };

  if (loading && customers.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading customers...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-w-0 space-y-6" data-testid="customers-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-extrabold sm:text-3xl">Customers</h1>
          <p className="text-muted-foreground">View and manage your customer database.</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-customers"
            />
          </div>
          <Button type="submit" className="w-full sm:w-auto">Search</Button>
        </form>

        {/* Customer List */}
        {customers.length === 0 ? (
          <div className="card-base !px-4 !py-8 text-center sm:!px-6 sm:!py-12">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-heading font-bold text-lg mb-2">No customers yet</h3>
            <p className="text-muted-foreground">
              Customers will appear here once they book appointments.
            </p>
          </div>
        ) : (
          <div className="grid min-w-0 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {customers.map(customer => (
              <Link
                key={customer.id}
                to={`/dashboard/customers/${customer.id}`}
                className="card-base card-interactive min-w-0 overflow-hidden !p-4 sm:!p-6"
                data-testid={`customer-${customer.id}`}
              >
                <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 sm:h-12 sm:w-12">
                    <span className="font-heading font-bold text-lg">
                      {(customer.name?.[0] || customer.email[0]).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold truncate">
                      {customer.name || 'Unnamed Customer'}
                    </h3>
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <div className="flex min-w-0 items-center gap-2">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                      {customer.phone && (
                        <div className="flex min-w-0 items-center gap-2">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{customer.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{customer.appointment_count} appointments</span>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomersPage;
